import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import "./InvoiceView.css";
import { getInvoice } from "../services/api";
import { FiArrowLeft, FiDownload } from 'react-icons/fi';

const InvoiceView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const invoiceRef = useRef();

  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      const { data } = await getInvoice(id);
      setInvoice(data);
    };
    fetchInvoice();
  }, [id]);

  const fmt = (v) => parseFloat(v).toLocaleString("en-IN") + "/-";

  // Normalize invoice number: VIECPL/2026-27/0002 → VIECPL0002
  const normalizeInvNum = (num) => {
    if (!num || !num.includes('/')) return num;
    const parts = num.split('/');
    return `VIECPL${parts[parts.length - 1]}`;
  };

  const fmtDate = (d) => {
    const date = new Date(d);
    return `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${date.getFullYear()}`;
  };

  const download = async () => {
    try {
      const element = invoiceRef.current;

      // Render the invoice element to a high-res PNG using the browser's own SVG engine
      const dataUrl = await toPng(element, {
        pixelRatio: 3,
        backgroundColor: '#ffffff',
        cacheBust: true,
      });

      // Create A4 PDF and fit the image to the page width
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();

      const img = new Image();
      img.src = dataUrl;
      await new Promise(resolve => { img.onload = resolve; });

      const imgH = (img.height * pdfW) / img.width;
      pdf.addImage(dataUrl, 'PNG', 0, 0, pdfW, Math.min(imgH, pdfH));
      pdf.save(`${normalizeInvNum(invoice.invoice_number)}.pdf`);
    } catch (error) {
      console.error('PDF generation failed:', error);
    }
  };

  const toWords = (num) => {
    const a = [
      "", "One", "Two", "Three", "Four", "Five", "Six", "Seven",
      "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen",
      "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"
    ];
    const b = [
      "", "", "Twenty", "Thirty", "Forty", "Fifty",
      "Sixty", "Seventy", "Eighty", "Ninety"
    ];
    if (num === 0) return "Zero";

    const convert = (n) => {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + " " + a[n % 10];
      if (n < 1000)
        return a[Math.floor(n / 100)] + " Hundred " + convert(n % 100);
      if (n < 100000)
        return convert(Math.floor(n / 1000)) + " Thousand " + convert(n % 1000);
      if (n < 10000000)
        return convert(Math.floor(n / 100000)) + " Lakh " + convert(n % 100000);
      return convert(Math.floor(n / 10000000)) + " Crore " + convert(n % 10000000);
    };

    return convert(Math.round(num)).replace(/\s+/g, " ").trim();
  };

  if (!invoice) return null;

  const same = invoice.client_state?.toLowerCase() === "telangana";

  return (
    <div className="invoice-page">

      <div className="iv-action-bar no-print">
        <div className="iv-bar-left">
          <img src="/veramasalogo.png" alt="Veramasa" className="iv-bar-logo" />
        </div>
        <div className="iv-bar-right">
          <button className="iv-back-btn" onClick={() => navigate("/dashboard")}>
            <FiArrowLeft size={16} />
            Back to Dashboard
          </button>
          <button className="iv-download-btn" onClick={download}>
            <FiDownload size={16} />
            Download PDF
          </button>
        </div>
      </div>

      <div className="invoice-paper" ref={invoiceRef}>

        <div style={{ border: "0.7px solid #000", paddingRight: "20px", paddingLeft: "20px" }}>
          {/* WATERMARK */}
          <div className="watermark">
            <p>Veramasa</p>
          </div>

          {/* HEADER */}
          <div className="header-box">
            <div className="header">
              <img src="/v-logo.png" className="logo-left" alt="V logo" />
              <img src="/veramasa-text.png" className="logo-right" alt="Veramasa logo" />
            </div>
          </div>

          <div className="title">TAX INVOICE</div>

          <table className="invoice-table">
            <tbody>

              <tr>
                <td colSpan="2" rowSpan="6" className="company-box">
                  <b>VERAMASA IT AND EDUCATION CONSULTANCY PVT LTD.</b><br />
                  <p style={{fontWeight:"lighter",marginTop:"7px"}}>2-2-1130/8/1/A, Shivam Road, New Nallakunta,
                  Hyderabad, Telangana 500044</p><br />
                </td>

                <td className="label">Invoice No</td>
                <td className="label" style={{fontWeight:"lighter"}}>{normalizeInvNum(invoice.invoice_number)}</td>
              </tr>

              <tr>
                <td className="label">SAC CODE</td>
                <td className="label" style={{fontWeight:"lighter"}}>{invoice.sac_code}</td>
              </tr>

              <tr>
                <td className="label">Date</td>
                <td className="label" style={{fontWeight:"lighter"}}>{fmtDate(invoice.invoice_date)}</td>
              </tr>

              <tr>
                <td className="label">GSTN</td>
                <td className="label" style={{fontWeight:"lighter"}}>{invoice.gstn || '36AAECV8279M1ZG'}</td>
              </tr>

              <tr>
                <td className="label">PAN No</td>
                <td className="label" style={{fontWeight:"lighter"}}>{invoice.pan_no || 'AAECV8279M'}</td>
              </tr>

              <tr>
                <td className="label">PO NO</td>
                <td className="label" style={{fontWeight:"lighter"}}>{invoice.po_no || 'NIL'}</td>
              </tr>

              <tr>
                <td colSpan="2" className="center"><b>Customer</b></td>

                <td className="label">PO DATE</td>
                <td className="label" style={{fontWeight:"lighter"}}>{invoice.po_date || 'NIL'}</td>
              </tr>

              <tr>
                <td colSpan="2" rowSpan="4" className="customer-box">
                  <b>{invoice.client_name}</b><br />
                  <p style={{ fontWeight:"lighter"}}>{invoice.client_address}</p><br />
                  <p style={{ fontWeight:"lighter",marginTop:"6px" }}>GSTIN: {invoice.client_gst}</p>
                </td>

                <td className="label">BANK</td>
                <td className="label" style={{fontWeight:"lighter"}}>{invoice.bank_name || 'AXIS BANK'}</td>
              </tr>

              <tr>
                <td className="label">Address</td>
                <td className="label" style={{fontWeight:"lighter"}}>{invoice.bank_address || 'DD COLONY Hyderabad'}</td>
              </tr>

              <tr>
                <td className="label">Account Number</td>
                <td className="label" style={{fontWeight:"lighter"}}>{invoice.account_number || '920020047084756'}</td>
              </tr>

              <tr>
                <td className="label">IFSC Code</td>
                <td className="label" style={{fontWeight:"lighter"}}>{invoice.ifsc_code || 'UTIB0004188'}</td>
              </tr>

              <tr>
                <td colSpan="2" style={{ borderRight: "none" }}></td>
                <td className="center"><b>Category</b></td>
                <td className="center" style={{fontWeight:"lighter"}}><b>Service</b></td>
              </tr>

              <tr>
                <td colSpan="4"></td>
              </tr>

              <tr className="service-head">
                <td>S.No</td>
                <td>Description</td>
                <td></td>
                <td>Total Value (INR)</td>
              </tr>

              <tr>
                <td className="center">1</td>
                <td style={{textAlign: "center",fontWeight:"lighter",fontSize:"14px"}}>{invoice.description}</td>
                <td style={{ textAlign: "center", fontWeight:"lighter",fontSize:"14px" }}>Base Price</td>
                <td className="right" style={{ textAlign: "center" }}>{fmt(invoice.amount)}</td>
              </tr>


              <tr>
                <td colSpan="3" className="right" style={{ textAlign: "right", fontWeight:"lighter" }}>CGST @ 9%</td>
                <td className="right" style={{ textAlign: "center" }}>{same ? fmt(invoice.cgst) : "Nil"}</td>
              </tr>

              <tr>
                <td colSpan="3" className="right" style={{ textAlign: "right", fontWeight:"lighter" }}>SGST @ 9%</td>
                <td className="right" style={{ textAlign: "center" }}>{same ? fmt(invoice.sgst) : "Nil"}</td>
              </tr>
              <tr>
                <td colSpan="3" className="right" style={{ textAlign: "right", fontWeight:"lighter" }}><b>IGST @ 18%</b></td>
                <td className="right" style={{ textAlign: "center" }}>{!same ? fmt(invoice.igst) : "Nil"}</td> </tr>
              <tr>
                <td colSpan="3" className="right" style={{ textAlign: "right",  }}>
                  Grand Total
                </td>
                <td className="right" style={{ textAlign: "center" }}>
                  {fmt(invoice.total_amount)}
                </td>
              </tr>

              <tr>
                <td className="words" colSpan="4" style={{ textAlign: "center", borderTop: "none" }}>
                  Amount Chargeable ( in words ) :
                  <i> INR {toWords(parseFloat(invoice.total_amount))} Only</i>
                </td>
              </tr>

            </tbody>
          </table>

          {/* FOOTER */}
          <div className="invoice-footer">

            <img src="/seal.png" className="seal" alt="seal" />

            <div className="sign-area">
              <div className="sub">
                For VERAMASA IT AND EDUCATION<br />
                CONSULTANCY Pvt Ltd.
              </div>

              <img src="/sign.png" className="sign" alt="signature" />

              <div className="auth">Authorized Signature</div>
            </div>

          </div>

          <div className="bottom-address">
            <b>Veramasa IT and Education Consultancy Pvt Ltd</b><br />
            <p style={{ fontSize: "12px", marginBottom: "20px" }}>2-2-1130/8/1/A, Shivam Road, New Nallakunta, Hyderabad, Telangana 500044,<br />
              website : <a href="https://veramasa.com/" target="_blank" rel="noreferrer" style={{ color: "#0a3d8f", textDecoration: "none" }}>www.veramasa.com</a> &nbsp;|&nbsp; Email ID : <span style={{ color: "#0a3d8f" }}>info@veramasa.com</span> &nbsp;|&nbsp; Phone : +91 90593 68510<br />
              GSTN : 36AAECV8279M1ZG &nbsp;|&nbsp; CIN : U80302TG2014PTC094536 </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceView;